/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { Link } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useGridApiRef, GridActionsCellItem } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import { useFormik } from 'formik';
import * as yup from 'yup';
import AskDialog from '../../components/common/dialog/AskDialog';
import * as empApi from '../../lib/api/employee';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import useActionPermission from '../../hooks/useActionPermission';

import { listEmployees } from '../../modules/empModule/employees';
import EmployeeList from '../../components/employeemng/EmployeeList';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import { EmployeeListBox } from '../../components/employeemng/EmployeeListBox';
import EmpOrdList from '../../components/common/form/modal/EmpOrdList';

import _ from 'lodash';

const EmpListContainer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [removeId, setRemoveId] = useState(null);
  const [orderName, setOrderName] = useState('');
  const [searchYn, setSearchYn] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('usrNm');
  const [searchWords, setsearchWords] = useState(null);

  const searchList = (values) => {
    //console.log(values, 'values');
    let searchData = {
      orderNm: orderName,
      useYn: !values.searchYn ? 'Y' : 'N',
      [values.searchKeyword]: values.searchWords,
      // 인사정보 가시범위(본인/부서장/전체권한) 적용 - 사원관리 리스트 화면 전용.
      // 사용자 피커/결재선 선택 등 다른 화면(EmpListContainer.js 외 listEmployees 호출부)은
      // 이 플래그를 보내지 않아 영향 없음. bito 4단계(2026-10-20).
      scopeApply: true,
    };

    dispatch(listEmployees(searchData));
    setSearchKeyword(values.searchKeyword);
    setsearchWords(values.searchWords);
    setSearchYn(values.searchYn);
  };

  const { employees, error, loading } = useSelector(
    ({ empModule, loading }) => ({
      employees: empModule.employees.employees,
      error: empModule.employees.error,
      loading: loading['employee/LIST_EMPLOYEES'],
    }),
  );

  const apiRef = useGridApiRef();

  // HR_RESUME_VIS_ADMIN은 어떤 사용자/부서에도 부여되지 않는 코드라 allowed는 사실상
  // "관리자인가"와 같다(useActionPermission: isAdmin이면 무조건 allowed=true). bito 4단계(2026-10-20).
  // 등록/수정/삭제/권한변경 버튼 노출 판단에 쓴다 - 실제 차단은 서버(UserController 등)가 한다.
  const { allowed: isAdmin } = useActionPermission('HR_RESUME_VIS_ADMIN');

  const [roleTarget, setRoleTarget] = useState(null);

  const handleRoleToggleClick = (row) => (event) => {
    event.stopPropagation();
    setRoleTarget(row);
  };

  const isAdminRow = (row) =>
    typeof row?.role === 'string' && row.role.toUpperCase().includes('ADMIN');

  const onRoleCancel = () => setRoleTarget(null);

  const onRoleConfirm = async () => {
    if (!roleTarget) return;
    const nextRole = isAdminRow(roleTarget) ? 'USER_ROLE' : 'ADMIN_ROLE';
    try {
      await empApi.changeUsrRole(roleTarget.usrUuid, nextRole);
      enqueueSnackbar(
        nextRole === 'ADMIN_ROLE'
          ? '관리자로 변경했습니다.'
          : '일반 사용자로 변경했습니다.',
        { variant: 'success' },
      );
      searchList(formik.values);
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
    setRoleTarget(null);
  };

  const handleRowEditStart = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleRowEditStop = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleCellFocusOut = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleEditClick = (id) => (event) => {
    event.stopPropagation();
    apiRef.current.startRowEditMode(id, 'edit');
  };

  const handleSaveClick = (id) => (event) => {
    event.stopPropagation();
    apiRef.current.commitRowChange(id);
    apiRef.current.startRowEditMode(id, 'view');

    const row = apiRef.current.getRow(id);

    //edit api call
    onSave(row);
  };

  const handleDeleteClick = (id) => (event) => {
    event.stopPropagation();

    //remove 작업
    setVisible(true);
    setRemoveId(id);
  };

  const handleCancelClick = (id) => (event) => {
    event.stopPropagation();
    apiRef.current.startRowEditMode(id, 'view');

    const row = apiRef.current.getRow(id);
    if (row.isNew) {
      apiRef.current.updateRows([{ id, _action: 'delete' }]);
    }
  };

  const onSave = async (data) => {
    const empData = _.clone(data);
    empData.useYn = empData.useYn === true ? 'Y' : 'N';

    try {
      const response = await empApi.editEmployee(empData);

      if (response.data.result === 'success') {
        apiRef.current.updateRows([{ ...data, isNew: false }]);
        enqueueSnackbar('수정 성공', {
          variant: 'success',
        });
      }
    } catch (err) {
      //apiRef.current.updateRows([{ id: data.id, _action: 'delete' }]);
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const onRemove = async () => {
    try {
      const res = await empApi.removeEmployee(removeId);

      if (res.data.info) {
        searchList(formik.values);
        enqueueSnackbar('미사용 처리 성공', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
    setRemoveId(null);
  };

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        hide: true,
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'usrNo',
        headerName: '사번',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (value) => value,
        renderCell: (params) => (
          <Link
            underline="none"
            component={RouterLink}
            to={`/employeemng/${params.row.usrUuid}`}
          >
            {params.row.usrNo}
          </Link>
        ),
      },
      {
        field: 'dptNm',
        headerName: '부서명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
      },
      {
        field: 'usrNm',
        headerName: '이름',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'gradeNm',
        headerName: '직급',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'telNo',
        headerName: '연락처',
        minWidth: 120,
        flex: 0.6,
        type: 'string',
        // 서버(PUT /api/v1/user/info/{usrUuid})가 관리자만 허용하므로 비관리자는 그리드에서도
        // 편집을 시작할 수 없게 막는다(bito 권한 체계 검토 2026-10-23).
        editable: isAdmin,
      },
      {
        field: 'emlAddr',
        headerName: '이메일',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: isAdmin,
      },
      {
        field: 'role',
        headerName: '권한',
        minWidth: 90,
        flex: 0.5,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Chip
            size="small"
            label={isAdminRow(params.row) ? '관리자' : '일반'}
            color={isAdminRow(params.row) ? 'primary' : 'default'}
            variant={isAdminRow(params.row) ? 'filled' : 'outlined'}
          />
        ),
      },
      // {
      //   field: 'gender',
      //   headerName: '성별',
      //   minWidth: 80,
      //   flex: 0.6,
      //   type: 'string',
      //   editable: false,
      //   align: 'center',
      //   headerAlign: 'center',
      //   valueFormatter: ({ value }) => (value === 'M' ? '남성' : '여성'),
      //   renderCell: (params) => (params.value === 'M' ? '남성' : '여성'),
      // },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 140,
        cellClassName: 'actions',
        getActions: ({ id, row }) => {
          const isInEditMode = apiRef.current.getRowMode(id) === 'edit';

          if (isInEditMode) {
            return [
              <GridActionsCellItem
                icon={<SaveIcon />}
                label="Save"
                onClick={handleSaveClick(id)}
                color="primary"
              />,
              <GridActionsCellItem
                icon={<CancelIcon />}
                label="Cancel"
                className="textPrimary"
                onClick={handleCancelClick(id)}
                color="inherit"
              />,
            ];
          }

          // 등록/수정/삭제는 서버가 관리자만 허용한다(UserController) - 비관리자는 아이콘 자체를
          // 숨긴다. 실제 차단은 서버가 한다(bito 권한 체계 검토 2026-10-23).
          if (!isAdmin) {
            return [];
          }

          return [
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(id)}
              color="inherit"
            />,
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={handleDeleteClick(id)}
              color="inherit"
            />,
            <GridActionsCellItem
              icon={isAdminRow(row) ? <PersonIcon /> : <AdminPanelSettingsIcon />}
              label={isAdminRow(row) ? '일반으로 변경' : '관리자로 변경'}
              onClick={handleRoleToggleClick(row)}
              color="inherit"
            />,
          ];
        },
      },
    ],
    [
      apiRef,
      isAdmin,
      handleEditClick,
      handleSaveClick,
      handleCancelClick,
      handleDeleteClick,
      handleRoleToggleClick,
    ],
  );

  const [visible, setVisible] = useState(false);

  const onCancle = () => {
    setVisible(false);
    setRemoveId(null);
  };

  const onConfirm = () => {
    setVisible(false);
    onRemove();
  };

  const handleAdd = () => {
    navigate('/employeemng/add');
  };

  // 검색 카드 "초기화" - 이 화면은 별도 reset 액션이 없고 로컬 state라, 초기값으로
  // 직접 되돌리고 다시 조회한다(2026-10-22, taxIssuedmng과 동일한 패턴).
  const handleReset = () => {
    searchList({ searchYn: false, searchKeyword: 'usrNm', searchWords: '' });
  };

  //검색 관련 사항
  const initialValues = {
    searchYn: searchYn,
    searchKeyword: searchKeyword,
    searchWords: searchWords,
  };

  const validationSchema = yup.object({
    searchYn: yup.boolean(),
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        searchList(values);
        setSubmitting(false);
      }, 1000);
    },
  });

  useEffect(() => {
    searchList(formik.values);
  }, [orderName]);

  const onChangeOrder = (order) => {
    if (order === orderName) {
      order = '';
    }

    setOrderName(order);
  };

  return (
    <>
      <BreadcrumbsContainer
        title="인사관리/사원관리/사원 리스트"
        icon="user"
        action={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {isAdmin && (
              <Button color="secondary" variant="outlined" onClick={() => navigate('/employeemng/visibility')}>
                이력 공개 설정
              </Button>
            )}
            {isAdmin && (
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAdd}>
                추가
              </Button>
            )}
          </Box>
        }
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <EmployeeListBox formik={formik} onReset={handleReset} />
        <EmpOrdList orderName={orderName} onChangeOrder={onChangeOrder} />
        <EmployeeList
          apiRef={apiRef}
          columns={columns}
          employees={employees}
          error={error}
          loading={loading}
          handleRowEditStart={handleRowEditStart}
          handleRowEditStop={handleRowEditStop}
          handleCellFocusOut={handleCellFocusOut}
            columnVisibilityModel={{
            // Hide columns status and traderName, the other columns will remain visible
            id: false,
          }}
        />
      </Box>
      <AskDialog
        visible={visible}
        title="사원 미사용"
        description="선택한 사원을 미사용 처리하시겠습니까?"
        confirmText="미사용"
        onConfirm={onConfirm}
        onCancle={onCancle}
      />
      <AskDialog
        visible={Boolean(roleTarget)}
        title="권한 변경"
        description={
          roleTarget
            ? isAdminRow(roleTarget)
              ? `${roleTarget.usrNm}님을 일반 사용자로 변경하시겠습니까?`
              : `${roleTarget.usrNm}님을 관리자로 지정하시겠습니까?`
            : ''
        }
        confirmText="변경"
        onConfirm={onRoleConfirm}
        onCancle={onRoleCancel}
      />
    </>
  );
};

export default EmpListContainer;
